/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useCallback, useMemo } from 'react';

import { css as cssUnbounded } from '@compiled/react';
// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
import { v4 as uuidv4 } from 'uuid';

import { css, jsx } from '@atlaskit/css';
import { type JSONDocNode } from '@atlaskit/editor-json-transformer';

import type {
	BlurInputMutableRefObject,
	ClearInputMutableRefObject,
	FocusInputMutableRefObject,
} from '../PromptForm/useSetInputRef';

// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage
const promptEditorWrapperStylesUnbounded = cssUnbounded({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'div > .ProseMirror': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'> p': {
			// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
			lineHeight: '20px',
		},

		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
		'> p:last-child': {
			marginBottom: 0,
			paddingBottom: 0,
		},

		/**
		 * Minimum height is set to 125px for confluence inline comment editor (ProseMirror element)
		 * When Editor AI is invoked in inline comments, same css rule applies here as well.
		 * So overriding it here.
		 */
		minHeight: 'auto',
	},

	// Smart link's inline card view has 0.5em padding (7px or 8px)
	// Because of that scrollbar appearts in prompt input because of limited height available.
	// More details here https://product-fabric.atlassian.net/browse/EDF-455
	// So removed this top and bottom padding here.
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'.ProseMirror .inlineCardView-content-wrap .card': {
		paddingTop: 0,
		paddingBottom: 0,
	},
});

const promptEditorWrapperStyles = css({
	display: 'grid',
	position: 'relative',
	flex: 1,
});

// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage
const promptEditorWrapperOverflowStyles = cssUnbounded({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'div > .ProseMirror': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'> p': {
			// We need a tiny bit of padding to avoid inline nodes with borders being visually clipped
			// and wwe need to increase the line height to allow for when inline nodes are focused as
			// they add box-shadow styling of 1px
			// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
			paddingTop: '0.5px',
			// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
			lineHeight: '24px',
		},
	},
});

// The types PromptEditorType should mirror what is in the editor-ai-injected-editors package
// at `platform/packages/editor/editor-ai-injected-editors/src/editors/types.ts.
export type PromptEditorType = 'user-input' | 'interrogate';

// The types PromptEditorProps and PromptEditor should mirror what is in the editor-ai-injected-editors package
// at `packages/editor/editor-ai-injected-editors/src/editors/prompt.tsx`.
export type PromptEditorProps = {
	ariaLabel?: string;
	autoFocus?: boolean;
	defaultValue?: JSONDocNode;
	/**
	 * We have tied enabling links to ConvoAI FF through this prop.
	 */
	enableLinks?: boolean;
	isInvalid?: boolean;
	onADFChange?: (input: JSONDocNode) => void;
	onBlur?: () => void;
	onChange?: (value: { adf: JSONDocNode; text: string }) => void;
	onFocus?: () => void;
	onInputChange?: (input: string) => void;
	placeholder?: string;
	/**
	 * Using separate setter function for clear as focus and blur
	 *  is set from editorApi while clear from editorActions.
	 * They both can be available at different time.
	 * So it's safest to have separate setter function.
	 */
	setClearHandler?: (clearFn?: () => boolean) => void;
	setFocusHandlers?: (focusFn?: () => boolean, blurFn?: () => boolean) => void;
	submit?: () => void;
	type?: PromptEditorType;
};

export type PromptEditor = (props: PromptEditorProps) => JSX.Element;

export type PromptEditorWrapperProps = {
	autoFocus: boolean;
	blurInputRef?: BlurInputMutableRefObject;
	clearInputRef?: ClearInputMutableRefObject;
	defaultValue?: JSONDocNode;
	enableLinks?: boolean;
	focusInputRef?: FocusInputMutableRefObject;
	onADFChange?: (inputValue: JSONDocNode) => void;
	onBlur?: React.FocusEventHandler<HTMLDivElement>;
	onCompositionEnd?: React.CompositionEventHandler<HTMLDivElement>;
	onCompositionStart?: React.CompositionEventHandler<HTMLDivElement>;
	onFocus?: () => void;
	onInputChange?: (inputValue: string) => void;
	onKeyDown?: React.KeyboardEventHandler<HTMLDivElement>;
	placeholder?: string;
	PromptEditor: PromptEditor;
	submit?: () => void;
	type: 'user-input' | 'interrogate';
};

export const PromptEditorWrapper = ({
	type,
	PromptEditor,
	defaultValue,
	autoFocus,
	focusInputRef,
	blurInputRef,
	clearInputRef,
	placeholder,
	enableLinks,
	onInputChange,
	onADFChange,
	onBlur,
	onFocus,
	onKeyDown,
	onCompositionStart,
	onCompositionEnd,
	submit,
}: PromptEditorWrapperProps) => {
	const setFocusHandlers = useCallback(
		(blur?: () => boolean, focus?: () => boolean) => {
			if (blurInputRef) {
				blurInputRef.current = blur;
			}
			if (focusInputRef) {
				focusInputRef.current = focus;
			}
		},
		[blurInputRef, focusInputRef],
	);

	const setClearHandler = useCallback(
		(clear?: () => boolean) => {
			if (clearInputRef) {
				clearInputRef.current = clear;
			}
		},
		[clearInputRef],
	);

	// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
	const componentId = useMemo(() => `${placeholder}-${uuidv4()}`, [placeholder]);

	return (
		// eslint-disable-next-line @atlaskit/design-system/prefer-primitives, @atlassian/a11y/no-static-element-interactions
		<div
			css={[
				promptEditorWrapperStyles,
				promptEditorWrapperStylesUnbounded,
				promptEditorWrapperOverflowStyles,
			]}
			data-testid="ai-experience-prompt-input"
			onKeyDown={onKeyDown}
			// We're using this div to listen to focus/blur/composition events which bubble up.
			onFocus={onFocus}
			onBlur={onBlur}
			onCompositionStart={onCompositionStart}
			onCompositionEnd={onCompositionEnd}
		>
			<PromptEditor
				type={type}
				key={componentId}
				autoFocus={autoFocus}
				defaultValue={defaultValue}
				placeholder={placeholder}
				onInputChange={onInputChange}
				onADFChange={onADFChange}
				setFocusHandlers={setFocusHandlers}
				setClearHandler={setClearHandler}
				enableLinks={enableLinks}
				submit={submit}
			/>
		</div>
	);
};
